import { Plus, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  HEALTHS, METHODOLOGIES, PRIORITIES, RISK_STATUSES, SEVERITIES, newRowId, requiredProblems,
  type Health, type Methodology, type PlanDraft, type Priority,
} from './model'

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        {hint ? <p className="text-tiny text-text-muted">{hint}</p> : null}
      </div>
      {children}
    </section>
  )
}

function Area({ label, value, onChange, placeholder, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  )
}

const NONE = '__none'

function Pick<T extends string>({ label, value, onChange, options }: { label: string; value: T | ''; onChange: (v: T | '') => void; options: ReadonlyArray<{ id: T; label: string }> }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? '' : (v as T))}>
        <SelectTrigger><SelectValue placeholder="Not set" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Not set</SelectItem>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * Everything a good project plan answers, in the order a person thinks it through: why,
 * how, how we will know, when, and what could go wrong. The same form creates a project and
 * edits it later, so the two can never ask different questions.
 */
export type PlanStep = 'aim' | 'timeline' | 'scope'

function Required() { return <span className="text-danger" aria-hidden> *</span> }
function Problem({ show, children }: { show: boolean; children: ReactNode }) { return show ? <p className="text-tiny text-danger">{children}</p> : null }

export function PlanFields({ value, onChange, step, showErrors = false }: { value: PlanDraft; onChange: (next: PlanDraft) => void; step?: PlanStep; showErrors?: boolean }) {
  const on = (g: PlanStep) => !step || step === g
  const missing = new Set(requiredProblems(value).map((p) => p.key))
  const set = <K extends keyof PlanDraft>(key: K, v: PlanDraft[K]) => onChange({ ...value, [key]: v })

  return (
    <div className="space-y-6">
      {on('aim') ? <>
      <Section title="Aim & strategy" hint="Why this exists, and how we intend to get there.">
        <Area label="Aim *" rows={2} value={value.aim} onChange={(v) => set('aim', v)} placeholder="One sentence: what must be true when this is done?" />
        <Problem show={showErrors && missing.has('aim')}>Say why this project exists.</Problem>
        <Area label="Strategy" value={value.strategy} onChange={(v) => set('strategy', v)} placeholder="The approach, in a few lines: phases, key decisions, what we will do first." />
      </Section>

      <Section title="Success metrics *" hint="At least one number this project should move. Baseline is today; target is where it should end up.">
        {value.metrics.map((m, i) => (
          <div key={m.id} className="grid grid-cols-[1fr_auto] gap-2 rounded-md border border-border p-2.5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Input className="col-span-2 sm:col-span-4" aria-label="Metric" placeholder="e.g. Open L3 tickets" value={m.name} onChange={(e) => set('metrics', value.metrics.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <Input aria-label="Unit" placeholder="Unit" value={m.unit} onChange={(e) => set('metrics', value.metrics.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)))} />
              {(['baseline', 'target', 'current'] as const).map((k) => (
                <Input key={k} inputMode="decimal" aria-label={k} placeholder={k[0]!.toUpperCase() + k.slice(1)} value={m[k]} onChange={(e) => set('metrics', value.metrics.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />
              ))}
            </div>
            <Button type="button" variant="ghost" size="icon" aria-label="Remove metric" onClick={() => set('metrics', value.metrics.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => set('metrics', [...value.metrics, { id: newRowId(), name: '', unit: '', baseline: '', target: '', current: '' }])}>
          <Plus className="mr-1 size-3.5" /> Add a metric
        </Button>
        <Problem show={showErrors && missing.has('metrics')}>Add a metric with a name and a numeric target.</Problem>
        <Area label="Expected improvements" value={value.expectedOutcomes} onChange={(v) => set('expectedOutcomes', v)} placeholder="What gets better, for whom, and by how much — in words a client or a CEO would read." />
      </Section>

      </> : null}
      {on('timeline') ? <>
      <Section title="Timeline & ownership">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Start<Required /></Label><Input type="date" value={value.startDate} onChange={(e) => set('startDate', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Target end<Required /></Label><Input type="date" min={value.startDate || undefined} value={value.endDate} onChange={(e) => set('endDate', e.target.value)} /></div>
          <Pick<Priority> label="Priority *" value={value.priority} onChange={(v) => set('priority', v)} options={PRIORITIES} />
          <Pick<Methodology> label="How we run it" value={value.methodology} onChange={(v) => set('methodology', v)} options={METHODOLOGIES} />
          <Pick<Health> label="Health" value={value.health} onChange={(v) => set('health', v)} options={HEALTHS} />
          <div className="space-y-1.5"><Label>Budget</Label><Input value={value.budget} maxLength={100} onChange={(e) => set('budget', e.target.value)} placeholder="e.g. 3 engineers × 6 weeks, or $12k" /></div>
        </div>
        <Problem show={showErrors && missing.has('dates')}>Set a start date and a target end on or after it.</Problem>
        <Problem show={showErrors && missing.has('priority')}>Pick a priority.</Problem>
        {value.health && value.health !== 'on_track' ? <Area label="What is wrong, and what is being done" rows={2} value={value.healthNote} onChange={(v) => set('healthNote', v)} /> : null}
      </Section>

      <Section title="Milestones *" hint="The few dates that matter on the way — at least one, with a date.">
        {value.milestones.map((m, i) => (
          <div key={m.id} className="flex flex-wrap items-center gap-2">
            <Input className="min-w-40 flex-1" aria-label="Milestone" placeholder="e.g. Backlog triaged" value={m.name} onChange={(e) => set('milestones', value.milestones.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <Input className="w-40" type="date" aria-label="Due" value={m.date} onChange={(e) => set('milestones', value.milestones.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
            <label className="flex items-center gap-1.5 text-tiny"><input type="checkbox" checked={m.done} onChange={(e) => set('milestones', value.milestones.map((x, j) => (j === i ? { ...x, done: e.target.checked } : x)))} /> Done</label>
            <Button type="button" variant="ghost" size="icon" aria-label="Remove milestone" onClick={() => set('milestones', value.milestones.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => set('milestones', [...value.milestones, { id: newRowId(), name: '', date: '', done: false }])}><Plus className="mr-1 size-3.5" /> Add a milestone</Button>
        <Problem show={showErrors && missing.has('milestones')}>Add a milestone with a name and a date.</Problem>
      </Section>

      </> : null}
      {on('scope') ? <>
      <Section title="Scope" hint="Optional, but it settles arguments later.">
        <div className="grid gap-3 sm:grid-cols-2">
          <Area label="In scope" value={value.scopeIn} onChange={(v) => set('scopeIn', v)} />
          <Area label="Out of scope" value={value.scopeOut} onChange={(v) => set('scopeOut', v)} />
        </div>
      </Section>

      <Section title="Risks" hint="What could stop it, and what we will do about it.">
        {value.risks.map((r, i) => (
          <div key={r.id} className="space-y-2 rounded-md border border-border p-2.5">
            <div className="flex gap-2">
              <Input aria-label="Risk" placeholder="What could go wrong" value={r.text} onChange={(e) => set('risks', value.risks.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
              <Button type="button" variant="ghost" size="icon" aria-label="Remove risk" onClick={() => set('risks', value.risks.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>
            </div>
            <div className="grid gap-2 sm:grid-cols-[8rem_8rem_1fr]">
              <Select value={r.severity} onValueChange={(v) => set('risks', value.risks.map((x, j) => (j === i ? { ...x, severity: v as typeof r.severity } : x)))}>
                <SelectTrigger aria-label="Severity"><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={r.status} onValueChange={(v) => set('risks', value.risks.map((x, j) => (j === i ? { ...x, status: v as typeof r.status } : x)))}>
                <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
                <SelectContent>{RISK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Input aria-label="Mitigation" placeholder="Mitigation" value={r.mitigation} onChange={(e) => set('risks', value.risks.map((x, j) => (j === i ? { ...x, mitigation: e.target.value } : x)))} />
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => set('risks', [...value.risks, { id: newRowId(), text: '', severity: 'medium', mitigation: '', status: 'open' }])}><Plus className="mr-1 size-3.5" /> Add a risk</Button>
      </Section>

      <Section title="Assumptions, dependencies & stakeholders">
        <div className="grid gap-3 sm:grid-cols-2">
          <Area label="Assumptions" value={value.assumptions} onChange={(v) => set('assumptions', v)} placeholder="What we are counting on being true." />
          <Area label="Dependencies" value={value.dependencies} onChange={(v) => set('dependencies', v)} placeholder="Teams, systems or decisions this waits on." />
        </div>
        {value.stakeholders.map((s, i) => (
          <div key={i} className="flex gap-2">
            <Input aria-label="Stakeholder" placeholder="Name" value={s.name} onChange={(e) => set('stakeholders', value.stakeholders.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <Input aria-label="Role" placeholder="Role (sponsor, approver, informed…)" value={s.role} onChange={(e) => set('stakeholders', value.stakeholders.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)))} />
            <Button type="button" variant="ghost" size="icon" aria-label="Remove stakeholder" onClick={() => set('stakeholders', value.stakeholders.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => set('stakeholders', [...value.stakeholders, { memberId: null, name: '', role: '' }])}><Plus className="mr-1 size-3.5" /> Add a stakeholder</Button>
      </Section>
      </> : null}
    </div>
  )
}
